import {
  CmpInvoiceActionAvailabilityFieldsFragment,
  CmpInvoiceActionTypeEnum,
} from "../../../graphql/types";

type InvoiceActionAvailability = Pick<
  CmpInvoiceActionAvailabilityFieldsFragment,
  "action" | "available" | "reason"
>;

export type InvoicePaymentCapability = {
  available: boolean;
  reason: string | null;
};

export type InvoicePaymentCapabilities = {
  cardCheckout: InvoicePaymentCapability;
  manualPayment: InvoicePaymentCapability;
};

const unavailableCapability = (): InvoicePaymentCapability => ({
  available: false,
  reason:
    "We could not check which payment options are available. Refresh the page and try again.",
});

const getCapability = ({
  action,
  availableActions,
}: {
  action: CmpInvoiceActionTypeEnum;
  availableActions: ReadonlyArray<InvoiceActionAvailability>;
}): InvoicePaymentCapability => {
  const matches = availableActions.filter(
    (availableAction) => availableAction.action === action
  );

  if (matches.length !== 1) {
    return unavailableCapability();
  }

  return {
    available: matches[0].available,
    reason:
      matches[0].available
        ? null
        : matches[0].reason?.trim() || unavailableCapability().reason,
  };
};

/**
 * Uses the server action list as the only payment authority. Missing or
 * duplicate capabilities fail closed so a stale client cannot expose a card
 * or manual payment action that the backend will reject.
 */
export const getInvoicePaymentCapabilities = ({
  availableActions,
}: {
  availableActions: ReadonlyArray<InvoiceActionAvailability>;
}): InvoicePaymentCapabilities => ({
  cardCheckout: getCapability({
    action: CmpInvoiceActionTypeEnum.StartCardCheckout,
    availableActions,
  }),
  manualPayment: getCapability({
    action: CmpInvoiceActionTypeEnum.RecordManualPayment,
    availableActions,
  }),
});
